/* ============================================================
   TISSIRE INTERACTIONS — one deferred bundle, no dependencies
   1. Scroll reveals (IntersectionObserver)
   2. Hero parallax (rAF + passive, desktop only)
   3. Add-to-cart pulse animation
   4. Word-split heading reveals
   5. Count-up animation for stat/trust numbers
   6. Scroll progress bar (product pages)
   ============================================================ */

(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;


  /* ── 1. SCROLL REVEALS ────────────────────────────────────── */
  var REVEAL_SELECTORS = [
    '.ta-clist__card',
    '.ta-rugcard',
    '.ta-glass',
    '.ta-hero-review',
    '.ta-review-grid__header',
    '.ta-review-grid__desktop .ta-rcard',
    '.ta-rugs__header',
    '.ta-transition__text',
    '.ta-process__cta',
    '.ta-process__step',
  ];

  function initReveals() {
    if (!('IntersectionObserver' in window)) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('ta-is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -60px 0px', threshold: 0.08 });

    REVEAL_SELECTORS.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el, i) {
        el.classList.add('ta-reveal');
        el.style.transitionDelay = Math.min(i * 80, 400) + 'ms';
        io.observe(el);
      });
    });

    /* Clip-path image reveals */
    if (!prefersReducedMotion) {
      document.querySelectorAll('.ta-glass__media, .ta-hero-review__img').forEach(function (el) {
        el.classList.add('ta-img-reveal');
        io.observe(el);
      });
    }
  }


  /* ── 2. HERO PARALLAX ─────────────────────────────────────── */
  function initParallax() {
    var mq = window.matchMedia(
      '(min-width: 1024px) and (prefers-reduced-motion: no-preference)'
    );
    if (!mq.matches) return;

    var heroMedia = document.querySelector('.ta-hero__media');
    if (!heroMedia) return;

    var hero = heroMedia.closest('.ta-hero');
    if (!hero) return;

    var ticking = false;

    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        var scrollY = window.scrollY;
        if (scrollY < hero.offsetTop + hero.offsetHeight) {
          heroMedia.style.transform =
            'translate3d(0,' + (scrollY * 0.28).toFixed(1) + 'px,0)';
        }
        ticking = false;
      });
    }, { passive: true });
  }


  /* ── 3. ADD-TO-CART PULSE ─────────────────────────────────── */
  function initCartPulse() {
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || form.getAttribute('action') !== '/cart/add') return;

      var btn = form.querySelector('.button, [type="submit"]');
      if (!btn) return;

      btn.classList.add('ta-cart-adding');
      setTimeout(function () {
        btn.classList.remove('ta-cart-adding');
        btn.classList.add('ta-cart-added');
        setTimeout(function () {
          btn.classList.remove('ta-cart-added');
        }, 1400);
      }, 600);
    });
  }


  /* ── 4. WORD-SPLIT HEADING REVEALS ───────────────────────── */
  var WORD_REVEAL_SELECTORS = [
    '.ta-page-hero__title',
    '.ta-hero__headline',
    '.ta-rugs__eyebrow',
    '.ta-review-grid__eyebrow',
    '.ta-clist__eyebrow',
  ];

  function splitWords(el) {
    var text = el.textContent.trim();
    if (!text) return;
    var words = text.split(/\s+/);
    el.textContent = '';
    el.classList.add('ta-word-reveal');

    words.forEach(function (word, i) {
      var outer = document.createElement('span');
      outer.className = 'ta-word';
      if (i > 0) outer.style.marginLeft = '0.28em';

      var inner = document.createElement('span');
      inner.className = 'ta-word-inner';
      inner.textContent = word;

      outer.appendChild(inner);
      el.appendChild(outer);
    });
  }

  function initWordReveals() {
    if (prefersReducedMotion) return;
    if (!('IntersectionObserver' in window)) return;

    var wordIo = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('ta-is-visible');
          wordIo.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -40px 0px', threshold: 0.15 });

    WORD_REVEAL_SELECTORS.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) {
        if (el.querySelector('.ta-word') || el.children.length > 0) return;
        splitWords(el);
        wordIo.observe(el);
      });
    });
  }


  /* ── 5. COUNT-UP for trust / stat numbers ─────────────────── */
  function countUp(el, target, duration) {
    var startTime = null;
    var isFloat = String(target).indexOf('.') !== -1;
    var decimals = isFloat ? String(target).split('.')[1].length : 0;

    function step(ts) {
      if (!startTime) startTime = ts;
      var progress = Math.min((ts - startTime) / duration, 1);
      var eased = 1 - Math.pow(1 - progress, 3);
      var current = target * eased;
      el.textContent = isFloat ? current.toFixed(decimals) : Math.floor(current);
      if (progress < 1) requestAnimationFrame(step);
    }

    requestAnimationFrame(step);
  }

  function initCountUp() {
    if (prefersReducedMotion) return;
    if (!('IntersectionObserver' in window)) return;

    var countIo = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var target = parseFloat(el.dataset.countTo);
        if (isNaN(target)) return;
        var duration = parseInt(el.dataset.countDuration, 10) || 1600;
        countUp(el, target, duration);
        countIo.unobserve(el);
      });
    }, { threshold: 0.5 });

    document.querySelectorAll('[data-count-to]').forEach(function (el) {
      countIo.observe(el);
    });

    /* Star ratings must never display as 0.0 mid-animation — render static.
       Only plain stat counters (customer counts etc.) animate. */
    document.querySelectorAll('.ta-stat__number').forEach(function (el) {
      if (el.dataset.countTo) return;
      var raw = el.textContent.trim();
      var num = parseFloat(raw.replace(/[^0-9.]/g, ''));
      if (isNaN(num) || num === 0) return;
      el.dataset.countTo = num;
      el.textContent = '0';
      countIo.observe(el);
    });
  }


  /* ── 6. SCROLL PROGRESS BAR (product pages) ───────────────── */
  function initScrollProgress() {
    var template = document.querySelector('[data-template]');
    if (!template || template.dataset.template !== 'product') return;

    var bar = document.createElement('div');
    bar.id = 'ta-progress';
    document.body.appendChild(bar);
    document.body.classList.add('ta-product-page');

    var ticking = false;
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        var scrolled = window.scrollY;
        var total = document.documentElement.scrollHeight - window.innerHeight;
        bar.style.width = (total > 0 ? (scrolled / total) * 100 : 0).toFixed(2) + '%';
        ticking = false;
      });
    }, { passive: true });
  }


  /* ── INIT ─────────────────────────────────────────────────── */
  function init() {
    initReveals();
    initParallax();
    initCartPulse();
    initWordReveals();
    initCountUp();
    initScrollProgress();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();